package com.devi3east.bashbuddy.dashboard.ui.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.LocalIndication
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.indication
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.filled.CurrencyExchange
import androidx.compose.material.icons.filled.Place
import androidx.compose.material.icons.filled.Schedule
import androidx.compose.material3.AssistChip
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import bashbuddy.composeapp.generated.resources.Res
import bashbuddy.composeapp.generated.resources.profile_dummy
import coil3.compose.AsyncImage
import com.devi3east.bashbuddy.common.TrailingActionIcon
import com.devi3east.bashbuddy.features.common.util.DateTimeFormatUtil
import com.devi3east.bashbuddy.features.common.util.formatPrice
import com.devi3east.bashbuddy.features.events.common.data.models.TicketType
import com.devi3east.bashbuddy.purchase.domain.models.PurchaseItemUiModel

@Composable
fun DashboardPurchaseCard(
    items: List<PurchaseItemUiModel>,
    navigateToEventDetail: (String, String) -> Unit
) {
    val cardShape = RoundedCornerShape(16.dp)
    val interactionSource = remember { MutableInteractionSource() }
    var isExpanded by remember { mutableStateOf(false) }

    val event = items.firstOrNull()
    val eventTitle = event?.eventTitle ?: "Unknown Event"
    val eventId = event?.eventId ?: ""
    val slug = event?.slug ?: ""
    val organizer = event?.createdBy ?: "Unknown Organizer"
    val imageUrl = event?.eventImage ?: Res.drawable.profile_dummy
    val rawCity = event?.eventCity
    val rawState = event?.eventState
    val fiftyFiftyJackpot = event?.fiftyFiftyJackpot ?: 0

    val formattedDate = event?.startInstant?.let { DateTimeFormatUtil.instantToDateString(it) } ?: ""
    val formattedTime = event?.startInstant?.let { DateTimeFormatUtil.instantToTimeString(it) } ?: ""

    ElevatedCard(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp)
            .clip(cardShape)
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.1f),
                shape = cardShape
            )
            .indication(interactionSource = interactionSource, indication = LocalIndication.current)
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                onClick = { isExpanded = !isExpanded }
            ),
        shape = cardShape,
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.elevatedCardElevation()
    ) {
        Column {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(8.dp)
                    .height(110.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    AsyncImage(
                        model = imageUrl,
                        //onError = { println("AsyncImage_onError=${it.result.throwable}") },
                        modifier = Modifier
                            .size(90.dp)
                            .clip(RoundedCornerShape(16.dp)),
                        contentScale = ContentScale.Crop,
                        contentDescription = null
                    )
                    Spacer(modifier = Modifier.size(12.dp))
                    Column(
                        verticalArrangement = Arrangement.SpaceBetween,
                        modifier = Modifier.weight(1f)
                    ) {
                        Text(
                            text = eventTitle,
                            style = MaterialTheme.typography.titleSmall.copy(fontWeight = FontWeight.Bold),
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                        Text(
                            text = organizer,
                            style = MaterialTheme.typography.bodySmall.copy(color = MaterialTheme.colorScheme.primary),
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.padding(top = 2.dp)
                        )
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.padding(top = 4.dp)
                        ) {
                            Icon(
                                imageVector = Icons.Default.Schedule,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.6f),
                                modifier = Modifier.size(14.dp)
                            )
                            Spacer(modifier = Modifier.size(4.dp))
                            Text(
                                text = "$formattedDate • $formattedTime",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.8f),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.fillMaxWidth().padding(top = 2.dp),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(
                                    imageVector = Icons.Default.Place,
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.6f),
                                    modifier = Modifier.size(14.dp)
                                )
                                Spacer(modifier = Modifier.size(4.dp))
                                Text(
                                    text = "$rawCity, $rawState",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.8f),
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            }
                        }
                        if (fiftyFiftyJackpot > 0 ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.padding(top = 4.dp)
                            ) {
                                Icon(
                                    imageVector = Icons.Default.CurrencyExchange,
                                    contentDescription = "50/50 Jackpot Amount",
                                    tint = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.6f),
                                    modifier = Modifier.size(14.dp)
                                )
                                Spacer(modifier = Modifier.size(4.dp))
                                Text(
                                    text = "50/50 Jackpot: ${formatPrice(event?.fiftyFiftyJackpot)}",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onBackground.copy(alpha = 0.8f),
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            }
                        }
                    }
                }
                TrailingActionIcon(
                    onClick = { navigateToEventDetail(eventId, slug) },
                    icon = Icons.AutoMirrored.Filled.ArrowForward,
                    iconTint = MaterialTheme.colorScheme.primary,
                    backgroundColor = MaterialTheme.colorScheme.primaryContainer
                )
            }

            // Expanded section (unchanged)
            AnimatedVisibility(
                visible = isExpanded,
                enter = expandVertically(),
                exit = shrinkVertically()
            ) {
                Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)) {
                    items.groupBy { it.ticketId }.forEach { (_, ticketItems) ->
                        val ticketTitle = ticketItems.firstOrNull()?.ticketTitle ?: "Unknown Ticket"
                        val serialNumber = ticketItems.firstOrNull()?.serialNumber ?: "N/A"
                        val totalQuantity = ticketItems.sumOf { it.quantity }
                        val allChances = ticketItems.flatMap { it.chanceNumbers }
                        val ticketType = ticketItems.firstOrNull()?.ticketType

                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = 8.dp)
                                .border(
                                    width = 1.dp,
                                    color = MaterialTheme.colorScheme.outline,
                                    shape = RoundedCornerShape(16.dp)
                                )
                                .padding(12.dp)
                        ) {
                            Text(
                                text = "Ticket: $ticketTitle",
                                style = MaterialTheme.typography.bodyMedium,
                                fontWeight = FontWeight.Bold
                            )
                            if (ticketType == TicketType.PRINTED && serialNumber.isNotBlank()) {
                                Text(
                                    text = "Serial #: $serialNumber",
                                    style = MaterialTheme.typography.bodySmall
                                )
                            }
                            Text(
                                text = "Quantity: $totalQuantity",
                                style = MaterialTheme.typography.bodySmall
                            )

                            if (allChances.isNotEmpty()) {
                                val sortedChances = allChances.sortedBy { it.toIntOrNull() ?: Int.MAX_VALUE }
                                val firstLength = sortedChances.first().length
                                val maxPerRow = when {
                                    firstLength <= 4 -> 4
                                    firstLength <= 8 -> 3
                                    firstLength <= 12 -> 2
                                    else -> 1
                                }

                                val paddedChances = sortedChances + List(
                                    (maxPerRow - (sortedChances.size % maxPerRow)) % maxPerRow
                                ) { null }

                                Spacer(Modifier.height(8.dp))
                                LazyVerticalGrid(
                                    columns = GridCells.Fixed(maxPerRow),
                                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .heightIn(max = 200.dp)
                                ) {
                                    items(paddedChances) { chance ->
                                        if (chance != null) {
                                            AssistChip(
                                                onClick = {},
                                                label = {
                                                    Text(
                                                        text = chance,
                                                        textAlign = TextAlign.Center,
                                                        modifier = Modifier.fillMaxWidth()
                                                    )
                                                },
                                                modifier = Modifier.fillMaxWidth()
                                            )
                                        } else {
                                            Box(modifier = Modifier.fillMaxWidth())
                                        }
                                    }
                                }
                            } else {
                                Spacer(modifier = Modifier.height(8.dp))
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(12.dp)
                                        .background(
                                            color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.6f),
                                            shape = RoundedCornerShape(12.dp)
                                        )
                                        .padding(16.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                        Text(
                                            text = "Thank You for Your Support!",
                                            style = MaterialTheme.typography.bodyMedium.copy(
                                                fontWeight = FontWeight.Bold,
                                                color = MaterialTheme.colorScheme.primary
                                            )
                                        )
                                        Spacer(modifier = Modifier.height(6.dp))
                                        Text(
                                            text = "The organization has received your generous contribution. Once your raffle numbers are assigned, they'll appear here automatically.",
                                            style = MaterialTheme.typography.bodySmall.copy(
                                                color = MaterialTheme.colorScheme.onSurfaceVariant
                                            ),
                                            textAlign = TextAlign.Center
                                        )
                                    }
                                }
                            }
                        }
                        Spacer(Modifier.height(12.dp))
                    }
                }
            }
        }
    }
}